import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { CopyButton } from "@/components/copy-button";
import { ErrorText, PageHeader, Stat } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { formatBytes } from "@/lib/bytes";
import { fmtDateTime, timeAgo } from "@/lib/format";
import { useUnlocked } from "@/lib/hooks";
import { session } from "@/lib/session";

export const Route = createFileRoute("/_app/admin")({
  beforeLoad: () => {
    const s = session.get();
    if ((s.status === "unlocked" || s.status === "locked") && s.account.role !== "super_admin")
      throw redirect({ to: "/" });
  },
  component: AdminPage,
});

const ROLES = [
  { value: "user", label: "user" },
  { value: "super_admin", label: "super admin" },
];

function AdminPage() {
  const me = useUnlocked();
  const qc = useQueryClient();
  const accounts = useQuery({
    queryKey: ["admin", "accounts"],
    queryFn: () => api.admin.listAccounts(),
    select: (d) => d.accounts,
  });
  const invites = useQuery({
    queryKey: ["admin", "invites"],
    queryFn: () => api.admin.listInvites(),
    select: (d) => d.invites,
  });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"user" | "super_admin">("user");
  const [lastInvite, setLastInvite] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => api.admin.createInvite({ email: email.trim() ? email.trim() : null, role }),
    onSuccess: (r) => {
      setLastInvite(r.invite_url);
      setEmail("");
      void qc.invalidateQueries({ queryKey: ["admin", "invites"] });
    },
  });
  const status = useMutation({
    mutationFn: (input: { account_id: string; status: "active" | "suspended" }) => api.admin.setAccountStatus(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "accounts"] }),
  });
  const totalOps = accounts.data?.reduce((n, a) => n + a.op_count, 0) ?? 0;
  const totalBytes = accounts.data?.reduce((n, a) => n + a.blob_bytes, 0) ?? 0;
  return (
    <>
      <PageHeader title="Admin" description="Accounts and usage. Vault contents are never visible here." />
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Accounts" value={accounts.data?.length ?? "–"} />
        <Stat label="Ops stored" value={totalOps} />
        <Stat label="Blob storage" value={formatBytes(totalBytes)} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Accounts</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Devices</TableHead>
                  <TableHead className="text-right">Ops</TableHead>
                  <TableHead className="text-right">Blobs</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.data?.map((a) => (
                  <TableRow key={a.id} data-testid="account-row">
                    <TableCell>
                      <p className="font-medium">{a.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {a.email} · joined {fmtDateTime(a.created_at)}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge variant={a.role === "super_admin" ? "default" : "outline"}>{a.role}</Badge>{" "}
                      {a.status === "suspended" ? <Badge variant="destructive">suspended</Badge> : null}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{a.device_count}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.op_count}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatBytes(a.blob_bytes)}</TableCell>
                    <TableCell className="text-right">
                      {a.id !== me.account.id ? (
                        <Button
                          variant={a.status === "active" ? "destructive" : "outline"}
                          size="xs"
                          disabled={status.isPending}
                          onClick={() =>
                            status.mutate({ account_id: a.id, status: a.status === "active" ? "suspended" : "active" })
                          }
                        >
                          {a.status === "active" ? "Suspend" : "Reinstate"}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-3">
              <ErrorText error={status.error ?? accounts.error} />
            </div>
          </CardContent>
        </Card>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Invite someone</CardTitle>
              <CardDescription>
                Invites last 7 days and work once. Leave the email blank for an open invite.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="inv-email">Email (optional)</Label>
                <Input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={role} onValueChange={(r) => setRole(r ?? "user")} items={ROLES}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => create.mutate()} disabled={create.isPending} data-testid="create-invite">
                Create invite
              </Button>
              <ErrorText error={create.error} />
              {lastInvite ? (
                <div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-2">
                  <code className="min-w-0 flex-1 truncate font-mono text-xs" data-testid="invite-url">
                    {lastInvite}
                  </code>
                  <CopyButton value={lastInvite} label="Copy invite link" />
                </div>
              ) : null}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Recent invites</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {invites.data?.slice(0, 10).map((i) => (
                <div key={i.id} className="flex items-center justify-between gap-2">
                  <span className="truncate">{i.email ?? "open invite"}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {i.used_at
                      ? `used ${timeAgo(i.used_at)}`
                      : i.expires_at < Date.now()
                        ? "expired"
                        : `expires ${fmtDateTime(i.expires_at)}`}
                  </span>
                </div>
              ))}
              {invites.data?.length === 0 ? <p className="text-muted-foreground">None yet.</p> : null}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
